import { ApolloClient, ApolloLink } from '@apollo/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { baseClientOption } from 'apollo/client';
import { createAuthenticationLink, errorLink, mainLink } from 'apollo/link';
import ToastStack from 'components/desktop/shared/toast-stack';
import { IconEyeOffOutline, IconEyeOutline, IconLoading } from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import FormCheckbox from 'components/shared/form-checkbox';
import FormTextInput from 'components/shared/form-text-input';
import ThirdPartyAuthentication from 'components/shared/third-party-authentication';
import { SignInForm, signInFormDefaultValues } from 'forms';
import { FACEBOOK_OA_ID, GOOGLE_CLIENT_ID, SITE_MAP } from 'globalConstants';
import { SIGN_IN, SignInData, SignInVariables } from 'graphql/main/mutations';
import { USER, UserData } from 'graphql/main/queries';
import { useFlexMutation, useToast } from 'hooks';
import { includes } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { FacebookProvider } from 'react-facebook';
import { FormProvider, useForm } from 'react-hook-form';

const RightSection = () => {
  const router = useRouter();
  const { push, query } = router;
  const { redirect } = query;
  const { saveAtmnhaAuthentication, saveECommerceAuthentication, setCurrentUser } =
    useAuthenticationContext();
  const form = useForm<SignInForm>({
    defaultValues: signInFormDefaultValues,
  });
  const { handleSubmit } = form;
  const { toasts, addToast, removeToast } = useToast();
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [signIn, { loading: loadingSignIn }] = useFlexMutation<SignInData, SignInVariables>(
    'main',
    SIGN_IN,
  );

  const togglePassword = () => {
    setIsShowPassword(!isShowPassword);
  };
  const submit = async (formData: SignInForm) => {
    const { phone, password, savedLogin } = formData;
    const { data: signInData, errors: signInErrors } = await signIn({
      variables: {
        emailOrPhone: phone ?? '',
        password: password ?? '',
        savedLogin: includes(savedLogin, 'true') ? true : undefined,
      },
      onError: (error) => {
        addToast('error', error.message);
      },
    });
    if (signInErrors) {
      saveAtmnhaAuthentication?.(undefined);
      saveECommerceAuthentication?.(undefined);
      addToast('error', signInErrors?.[0]?.message);
    } else {
      const { signIn } = signInData ?? {};
      const { payload } = signIn ?? {};
      const { accessToken } = payload ?? {};
      if (accessToken) {
        saveAtmnhaAuthentication?.(payload);
        const authLink = createAuthenticationLink(accessToken);
        const mainClient = new ApolloClient({
          ...baseClientOption,
          link: ApolloLink.from([errorLink, authLink, mainLink]),
        });
        const { data: userData, error: userError } = await mainClient.query<UserData>({
          query: USER,
        });
        const { user } = userData;
        if (userError || !user) {
          saveAtmnhaAuthentication?.(undefined);
          saveECommerceAuthentication?.(undefined);
          addToast('error', userError ? userError.message : 'Lỗi xác thực!');
        } else {
          const { ecommerceData } = user ?? {};
          saveECommerceAuthentication?.(ecommerceData);
          setCurrentUser?.(user);
          addToast('success', 'Đăng nhập thành công');
          push(redirect ? `${redirect}` : '/');
        }
      }
    }
  };

  return (
    <FacebookProvider appId={FACEBOOK_OA_ID}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <div
          id='right-section'
          className='flex w-full flex-col justify-center space-y-[32px] bg-paper px-[24px] py-[40px]'
        >
          <h1 className='text-[20px] font-[600] leading-[25px]'>Đăng nhập</h1>
          <FormProvider {...form}>
            <form className='w-full space-y-[32px]'>
              <FormTextInput name='phone' label='Số điện thoại' autoComplete='off' required />
              <FormTextInput
                required
                name='password'
                type={isShowPassword ? 'text' : 'password'}
                label='Mật khẩu'
                autoComplete='off'
                endIcon={
                  isShowPassword ? (
                    <button type='button' onClick={togglePassword}>
                      <IconEyeOutline className='min-h-[24px] min-w-[24px] text-text2' />
                    </button>
                  ) : (
                    <button type='button' onClick={togglePassword}>
                      <IconEyeOffOutline className='min-h-[24px] min-w-[24px] text-text2' />
                    </button>
                  )
                }
              />
              <div className='flex flex-col space-y-[16px]'>
                <button
                  className={`flex h-[40px] w-full items-center justify-center space-x-[6px] rounded-[8px] bg-primary py-[8px] text-paper transition duration-[200ms] ease-in-out hover:bg-primary-light ${
                    loadingSignIn ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  onClick={handleSubmit(submit)}
                  disabled={loadingSignIn}
                >
                  {loadingSignIn && <IconLoading className='animate-spin' />}
                  <span>Đăng nhập</span>
                </button>
                <div className='flex w-full items-center justify-between'>
                  <FormCheckbox
                    name='savedLogin'
                    options={[
                      {
                        label: 'Nhớ tài khoản',
                        value: 'true',
                      },
                    ]}
                    optionClassName='text-[12px] leading-[15px]'
                  />
                  <Link
                    href={`/${SITE_MAP.RECOVERY.INDEX}`}
                    className='cursor-pointer text-[12px] leading-[15px] text-primary'
                  >
                    Quên mật khẩu?
                  </Link>
                </div>
              </div>
              <div className='flex items-center space-x-[24px] text-stroke'>
                <div className='h-[1px] w-full bg-stroke'></div>
                <span className='text-text2'>hoặc</span>
                <div className='h-[1px] w-full bg-stroke'></div>
              </div>
              <ThirdPartyAuthentication />
              <div className='text-center text-[12px] leading-[15px]'>
                <span>Bạn chưa là thành viên?</span>{' '}
                <Link href={`/${SITE_MAP.SIGN_UP.INDEX}`} className='font-[600] text-primary'>
                  Đăng ký
                </Link>{' '}
                <span>tại đây</span>
              </div>
            </form>
          </FormProvider>
        </div>
        <ToastStack toasts={toasts} removeToast={removeToast} />
      </GoogleOAuthProvider>
    </FacebookProvider>
  );
};
export default RightSection;
